import { useState } from 'react';
import { Button } from '../../components/Button';
import { ActionButton } from '../../components/Button/ActionButton';
import { ComponentsLayout } from './layout';

export const ButtonsPage = () => {
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const handleLoadingTest = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 2000);
  };

  const handlePendingTest = () => {
    setIsPending(true);
    setTimeout(() => setIsPending(false), 3000);
  };

  return (
    <ComponentsLayout 
      title="Buttons" 
      description="Componentes de botão para interações e ações"
    >
      {/* Seção de Buttons */}
      <div className="space-y-12">
          {/* Button Variants */}
          <section className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Button - Variantes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide">Principais</h3>
                <div className="space-y-3">
                  <Button variant="primary" className="w-full">Primary</Button>
                  <Button variant="secondary" className="w-full">Secondary</Button>
                  <Button variant="tertiary" className="w-full">Tertiary</Button>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide">Especiais</h3>
                <div className="space-y-3">
                  <Button variant="destructive" className="w-full">Destructive</Button>
                  <Button variant="outline" className="w-full">Outline</Button>
                  <Button variant="ghost" className="w-full">Ghost</Button>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide">Outros</h3>
                <div className="space-y-3">
                  <Button variant="link" className="w-full">Link</Button>
                  <Button disabled className="w-full">Disabled</Button>
                </div>
              </div>
            </div>
          </section>

          {/* Button Sizes */}
          <section className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Button - Tamanhos</h2>
            <div className="flex flex-wrap items-center gap-4">
              <Button variant="primary" size="sm">Small</Button>
              <Button variant="primary" size="default">Default</Button>
              <Button variant="primary" size="lg">Large</Button>
              <Button variant="primary" size="icon">🚀</Button>
            </div>
          </section>

          {/* Button Loading States */}
          <section className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Button - Estados de Loading</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-800">Button com isLoading</h3>
                <div className="space-y-3">
                  <Button 
                    variant="primary" 
                    isLoading={isLoading}
                    onClick={handleLoadingTest}
                    className="w-full"
                  >
                    {isLoading ? 'Carregando...' : 'Testar Loading (2s)'}
                  </Button>
                  <Button 
                    variant="secondary" 
                    isLoading={true}
                    loadingText="Processando..."
                    className="w-full"
                  >
                    Sempre Loading
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-800">ActionButton com isPending</h3>
                <div className="space-y-3">
                  <ActionButton 
                    variant="primary" 
                    isPending={isPending}
                    onClick={handlePendingTest}
                    className="w-full"
                  >
                    {isPending ? 'Enviando...' : 'Testar Pending (3s)'}
                  </ActionButton>
                  <ActionButton 
                    variant="outline" 
                    isPending={true}
                    className="w-full"
                  >
                    Sempre Pending
                  </ActionButton>
                </div>
              </div>
            </div>
          </section>

          {/* Interactive Demo */}
          <section className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Demo Interativo</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="text-center space-y-4">
                <div className="text-3xl font-bold text-blue-600">{count}</div>
                <p className="text-gray-600">Clique nos botões para incrementar o contador</p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button 
                    variant="primary" 
                    onClick={() => setCount(c => c + 1)}
                  >
                    +1
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={() => setCount(c => c + 5)}
                  >
                    +5
                  </Button>
                  <Button 
                    variant="tertiary" 
                    onClick={() => setCount(c => c + 10)}
                  >
                    +10
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => setCount(0)}
                  >
                    Reset
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Code Examples */}
          <section className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Exemplos de Código</h2>
            <div className="space-y-6">
              <div className="bg-gray-900 rounded-lg p-4 text-sm overflow-x-auto">
                <pre className="text-green-400">
{`// Button básico
<Button variant="primary">Clique aqui</Button>

// Button com loading
<Button variant="secondary" isLoading={isLoading}>
  Salvar
</Button>

// ActionButton para formulários
<ActionButton 
  variant="primary" 
  isPending={isPending}
  onClick={handleSubmit}
>
  Enviar Formulário
</ActionButton>`}
                </pre>
              </div>
            </div>
          </section>
        </div>
      </ComponentsLayout>
    )
  }
